import { ArrowDownIcon, ArrowUpIcon, CloseIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Skeleton,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tfoot,
  Tr,
  Tooltip,
  useColorModeValue
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useExpanded, useFilters, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable, useResizeColumns, useBlockLayout } from "react-table";
import './appTable.css';
import { useGetAllInsightsQuery } from 'store/BmAccounts/bmAccountApi';

// Custom cell wrapper component with tooltip
const CellWithTooltip = ({ children }) => {
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (element) {
      setIsOverflowing(element.scrollWidth > element.clientWidth);
    }
  }, [children]);

  const content = (
    <Box
      ref={textRef}
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
    >
      {children}
    </Box>
  );

  if (isOverflowing) {
    return (
      <Tooltip label={children} placement="top" hasArrow>
        {content}
      </Tooltip>
    );
  }

  return content;
};

function AppTableWithPageMeta({
  columns,
  data = [],
  searchEnabled,
  isLoading,
  isFetching,
  initialState,
  pageMeta = {},
  onPageChange,
  onSearchInputChange,
  searchTerm,
  emptyMessage,
  total,
  adsAccountId,
  datePreset,
  currencyCode
}) {

  const backgroundColor = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.800');

  const [showError, setShowError] = useState(false);
  const { pageSize, totalItems, after, before, hasNextPage, hasPrevPage } = pageMeta;

  // const {
  //   data: allInsights,
  //   isFetching: isAllInsightsListFetcing,
  //   refetch: refetchAllInsights,
  //   isLoading: isAllInsightsLoading,
  //   error: allInsightsError
  // } = useGetAllInsightsQuery(
  //   {
  //     adsAccountId,
  //     date_preset: datePreset,
  //   },
  //   { skip: !adsAccountId}
  // );

  const defaultColumn = React.useMemo(
    () => ({
      minWidth: 100,
      width: 150,
      maxWidth: 800,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      manualPagination: true,
      initialState: {
        pageIndex: initialState?.pageIndex || 0,
        pageSize: pageSize || 20,
        ...initialState,
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    useBlockLayout,
    useResizeColumns
  );

  /* SEARCH */
  const inputRef = useRef();
  const [searchValue, setsearchValue] = useState(searchTerm);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchValue?.length > 2) {
        onSearchInputChange(searchValue);
        setHasSearched(true);
      } else if (searchValue?.length === 0) {
        onSearchInputChange(searchValue);
        setHasSearched(false);
      }
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [searchValue, onSearchInputChange]);

  useEffect(() => {
    if (data?.length == 0) {
      setShowError(true);
      onSearchInputChange('');
    }
  }, [data, onSearchInputChange]);

  useEffect(() => {
    if (showError) {
      const id = setTimeout(() => setShowError(false), 3000);
      return () => clearTimeout(id);
    }
  }, [showError]);

  const handleNextPage = () => {
    if (pageMeta.hasNextPage) {
      const newPage = initialState.pageIndex + 1;
      onPageChange({ after, currentPage: newPage });
    }
  };

  const handlePreviousPage = () => {
    if (pageMeta.hasPrevPage) {
      const newPage = initialState.pageIndex - 1;
      onPageChange({ before, currentPage: newPage });
    }
  };

  const format = (v) => {
    const n = Number(v || 0);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 2,
    }).format(n);
  };

  // const columnCount = headerGroups?.[0]?.headers?.length || columns.length;
  // const totalCampaigns = typeof totalItems === 'number' ? totalItems : (Array.isArray(data) ? data.length : 0);
  // const totalSpentINR = format(allInsights?.summary?.[0]?.spend);

  // Find the index of the "Spend" column
  // const spendColumnIndex = headerGroups[0]?.headers?.findIndex(
  //   header => header.Header === "Spend" || header.id === "spend"
  // );

  const TopBar = (
    <Flex align="center" gap={4} mb="20px">
      {/* Search (80%) */}
      <Box flex={{ base: 1, md: 0.8 }}>
        {searchEnabled && (
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none" height="100%">
              <SearchIcon color="gray.300" />
            </InputLeftElement>

            <Input
              ref={inputRef}
              type="text"
              borderRadius="md"
              value={searchValue || ""}
              onChange={(e) => setsearchValue(e.target.value)}
              placeholder="Search..."
            />

            {searchValue && (
              <InputRightElement
                cursor="pointer"
                onClick={() => setsearchValue("")}
              >
                {isFetching ? (
                  <Spinner size={'sm'}/>
                ) : (
                  <CloseIcon fontSize={14} color="gray.300" _hover={{ color: "gray.600" }} />
                )}
              </InputRightElement>
            )}
          </InputGroup>
        )}

        {showError ? (
          <Flex mt={2} flexDirection="column" gap="10px" justifyContent="center" alignItems="flex-start">
            <Text fontSize="sm" color="red.500">No results found. Try a different search term.</Text>
          </Flex>
        ) : null}
      </Box>

      {/* Pagination (20%) */}
      <Flex
        flex={{ base: 1, md: 0.2 }}
        align="center"
        justify="flex-end"
        gap={2}
      >
        {total === 'noTotal' && data.length > 9 ? (
          <>
            <Text fontSize="sm">
              {`${pageSize * initialState.pageIndex + 1}-${pageSize * (initialState.pageIndex + 1)}`}
            </Text>
            <Flex gap={2}>
              <Button
                size="sm"
                onClick={handlePreviousPage}
                isDisabled={!hasPrevPage || isFetching}
              >
                <Icon as={FiChevronLeft} />
              </Button>
              <Button
                size="sm"
                onClick={handleNextPage}
                isDisabled={!hasNextPage || isFetching}
              >
                <Icon as={FiChevronRight} />
              </Button>
            </Flex>
          </>
        ) : totalItems >= 20 ? (
          <>
            <Text fontSize="sm">
              {totalItems > 0
                ? `${pageSize * initialState.pageIndex + 1}-${pageSize * (initialState.pageIndex + 1)} of ${totalItems}`
                : 'No data available'}
            </Text>
            <Flex gap={2}>
              <Button
                size="sm"
                onClick={handlePreviousPage}
                isDisabled={!hasPrevPage || isFetching}
              >
                <Icon as={FiChevronLeft} />
              </Button>
              <Button
                size="sm"
                onClick={handleNextPage}
                isDisabled={!hasNextPage || isFetching}
              >
                <Icon as={FiChevronRight} />
              </Button>
            </Flex>
          </>
        ) : null}
      </Flex>
    </Flex>
  );

  const ScrollableTable = (
    <TableContainer
      maxH="440px"
      overflowY="auto"
      overflowX="auto"
      borderWidth={1}
      borderColor={borderColor}
      sx={{ scrollbarGutter: 'stable both-edges' }}
    >
      {isLoading && <Skeleton height="5px" />}
  
      <Table
        className="sticky"
        variant="simple"
        size="md"
        {...getTableProps()}
      >
        <Thead
          bg={backgroundColor}
          position="sticky"
          top={0}
          zIndex={2}
          boxShadow="sm"
        >
          {headerGroups.map(headerGroup => (
            <Tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <Th 
                  {...column.getHeaderProps()}
                  position="relative"
                >
                <Box
                  {...column.getSortByToggleProps()} 
                  cursor="pointer"
                  flex="1"
                  minWidth="0"
                >
                  <Flex align="center" gap="10px" overflow="hidden">
                    <CellWithTooltip>
                      {column.render("Header")}
                    </CellWithTooltip>
                    {column.isSorted && (
                      <Box as="span" flexShrink={0}>
                        {column.isSortedDesc
                          ? (<ArrowDownIcon boxSize={3} />)
                          : (<ArrowUpIcon boxSize={3} />)}
                      </Box>
                    )}
                    <Box as="span" flexShrink={0}>
                      {column?.canFilter ? column.render("Filter") : null}
                    </Box>
                  </Flex>
                </Box>
                  
                  {/* Resize Handle - Visible Column Divider */}
                  <Box
                    {...column.getResizerProps()}
                    position="absolute"
                    right="-1px"
                    top="0"
                    height="100%"
                    width="8px"
                    cursor="col-resize"
                    userSelect="none"
                    touchAction="none"
                    zIndex={1}
                    _before={{
                      content: '""',
                      position: 'absolute',
                      left: '3px',
                      top: '0',
                      bottom: '0',
                      width: '1px',
                      bg: borderColor,
                      transition: 'all 0.2s'
                    }}
                    _hover={{
                      _before: {
                        width: '2px',
                        bg: '#8671cc',
                        boxShadow: '0 0 4px #8671cc'
                      }
                    }}
                    sx={{
                      ...(column.isResizing && {
                        _before: {
                          width: '2px',
                          bg: '#8671cc',
                          boxShadow: '0 0 4px #8671cc'
                        }
                      })
                    }}
                  />
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
  
        <Tbody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row);
            return (
              <React.Fragment key={i + 'row'}>
                <Tr {...row.getRowProps()}>
                  {row.cells.map(cell => (
                    <Td 
                      {...cell.getCellProps()} 
                      fontSize="sm"
                    >
                      <CellWithTooltip>
                        {cell.render("Cell")}
                      </CellWithTooltip>
                    </Td>
                  ))}
                </Tr>
              </React.Fragment>
            );
          })}
        </Tbody>
  
        {/* Sticky totals footer - Now aligned with columns */}
        {/* <Tfoot
          bg={useColorModeValue('gray.50', 'gray.700')}
          position="sticky"
          bottom={0}
          zIndex={2}
          boxShadow="0 -2px 4px rgba(0,0,0,0.06)"
        >
          <Tr>
            {headerGroups[0]?.headers.map((column, index) => (
              <Td 
                key={column.id}
                {...column.getFooterProps()}
                px={3}
                py={3}
                borderTop="none"
                borderBottom="none"
              >
                
                {index === 1 && (
                  <Box>
                    <Text fontSize="sm" fontWeight="medium">
                      Results from {totalCampaigns.toLocaleString()} campaigns
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      Excludes deleted items
                    </Text>
                  </Box>
                )}

                {index === spendColumnIndex && (
                  <Box textAlign="right">
                    {isLoading || isAllInsightsListFetcing || isAllInsightsLoading ? (
                      <Skeleton height="10px" width="60px" m={1} />
                    ) : (
                      <Text fontSize="sm" fontWeight="semibold">
                        {totalSpentINR}
                      </Text>
                    )}
                    <Text fontSize="xs" color="gray.500" lineHeight="shorter">
                      Total spent
                    </Text>
                  </Box>
                )}
              </Td>
            ))}
          </Tr>
        </Tfoot> */}
      </Table>
    </TableContainer>
  );
  

  return (
    <Box>
      {hasSearched ? (
        <Box>
          {TopBar}
          {ScrollableTable}
        </Box>
      ) : (
        <>
          {data?.length == 0 ? (
            <Flex justifyContent={'center'} alignItems={'center'} height={'50vh'}>
              {emptyMessage ? emptyMessage : 'No data available'}
            </Flex>
          ) : (
            <>
              {TopBar}
              {ScrollableTable}
            </>
          )}
        </>
      )}
    </Box>
  );
}

export default AppTableWithPageMeta;